import {
    Component,
    Signal,
    isSignal,
    ChangeDetectionStrategy,
    input,
} from '@angular/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { QueryObserverResult } from '@tanstack/query-core';
import { DataSource } from '../../models/charts/fusion-charts/data-source';

type SupportedDataTypes = GridDataResult | DataSource | any[] | any;

@Component({
    selector: 'app-control-status-indicator',
    templateUrl: './control-status-indicator.component.html',
    styleUrl: './control-status-indicator.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ControlStatusIndicatorComponent {
    readonly queryObserverResult = input<
        | QueryObserverResult<SupportedDataTypes, Error>
        | Signal<SupportedDataTypes | undefined>
        | null
    >();

    get isLoading(): boolean {
        const queryObserverResult = this.queryObserverResult();
        if (queryObserverResult == null) {
            return true;
        }

        if (isSignal(queryObserverResult)) {
            const value = queryObserverResult();
            if (value === undefined) {
                return true;
            }
            if (this.isQueryObserverResult(value)) {
                return !!(value.isLoading || value.isFetching);
            }
            return false;
        }

        if (this.isQueryObserverResult(queryObserverResult)) {
            return !!(
                queryObserverResult.isLoading || queryObserverResult.isFetching
            );
        }

        return false;
    }

    get shouldShowNoDataMessage(): boolean {
        const queryObserverResult = this.queryObserverResult();
        if (this.isQueryObserverResult(queryObserverResult)) {
            return (
                !queryObserverResult.isLoading &&
                this.showNoDataMessage(queryObserverResult.data)
            );
        }

        if (isSignal(queryObserverResult)) {
            const data = queryObserverResult();
            return !this.isLoading && this.showNoDataMessage(data);
        }

        // If it's neither a QueryObserverResult nor a Signal (or is null/undefined), treat as no data
        return !this.isLoading && this.showNoDataMessage(undefined);
    }

    private isQueryObserverResult(
        value: any,
    ): value is QueryObserverResult<SupportedDataTypes, Error> {
        // Ensure value is an object before using the `in` operator to avoid runtime errors
        return (
            value != null &&
            typeof value === 'object' &&
            ('isLoading' in value || 'isFetching' in value)
        );
    }

    private showNoDataMessage(data: SupportedDataTypes | undefined): boolean {
        if (!data) {
            return true;
        }

        if ('data' in data && 'total' in data) {
            return data?.data?.length === 0;
        } else if ('categories' in data && 'chart' in data) {
            return (
                (data?.data?.length ?? 0) === 0 &&
                (data?.dataset?.length ?? 0) === 0
            );
        } else if (Array.isArray(data)) {
            return data?.length === 0;
        } else {
            return false;
        }
    }
}
